import { ConnectedPosition, Overlay, OverlayRef, PositionStrategy } from '@angular/cdk/overlay'
import { ComponentPortal } from '@angular/cdk/portal'
import {
	ChangeDetectorRef,
	Directive,
	ElementRef,
	inject,
	InjectionToken,
	Injector,
	input,
	OnChanges,
	OnDestroy,
	TemplateRef,
	ViewContainerRef,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { isServer } from '@mos/client/shared/utils'
import { fromEvent, merge } from 'rxjs'
import { TooltipContainerComponent } from '../components/tooltip/tooltip-container.component'

export type TooltipPosition = 'top' | 'bottom' | 'left' | 'right'
export type TooltipData = string | TemplateRef<void>
export const TOOLTIP_DATA = new InjectionToken<TooltipData>('')

interface TooltipPositions {
	top: ConnectedPosition
	bottom: ConnectedPosition
	left: ConnectedPosition
	right: ConnectedPosition
}

@Directive({
	selector: '[mosTooltip]',
})
export class TooltipDirective implements OnDestroy, OnChanges {
	private changeDetector = inject(ChangeDetectorRef)
	private overlayRef: OverlayRef | null = null

	private element = inject(ElementRef<HTMLElement>)
	private overlay = inject(Overlay)
	private viewContainer = inject(ViewContainerRef)

	tooltip = input.required<TooltipData>({ alias: 'mosTooltip' })
	position = input<TooltipPosition>('top', { alias: 'mosTooltipPosition' })
	error = input(false, { alias: 'mosTooltipError', transform: () => true })

	ngOnChanges(): void {
		if (this.overlayRef?.hasAttached()) {
			this.hideTooltip()
			this.showTooltip()
		}
	}

	constructor() {
		if (isServer) {
			return
		}

		merge(
			fromEvent(this.element.nativeElement, 'mouseenter', { passive: true }),
			fromEvent(this.element.nativeElement, 'touchstart', { passive: true }),
			fromEvent(this.element.nativeElement, 'focus', { passive: true }),
		)
			.pipe(takeUntilDestroyed())
			.subscribe(() => {
				this.showTooltip()
			})

		merge(
			fromEvent(this.element.nativeElement, 'mouseleave', { passive: true }),
			fromEvent(this.element.nativeElement, 'touchend', { passive: true }),
			fromEvent(this.element.nativeElement, 'blur', { passive: true }),
		)
			.pipe(takeUntilDestroyed())
			.subscribe(() => {
				this.hideTooltip()
			})
	}

	showTooltip(): void {
		if (this.overlayRef?.hasAttached() === true) {
			return
		}

		this.attachTooltip()
		this.changeDetector.markForCheck()
	}

	hideTooltip(): void {
		if (this.overlayRef?.hasAttached() === true) {
			this.overlayRef?.detach()
		}
		this.changeDetector.markForCheck()
	}

	ngOnDestroy() {
		this.overlayRef?.dispose()
	}

	private attachTooltip() {
		if (this.overlayRef === null) {
			const positionStrategy = this.getPositionStrategy()
			this.overlayRef = this.overlay.create({ positionStrategy })
			if (this.error()) {
				this.overlayRef.addPanelClass('error')
			}
		}

		const injector = Injector.create({
			providers: [
				{
					provide: TOOLTIP_DATA,
					useValue: this.tooltip(),
				},
			],
		})
		const component = new ComponentPortal(TooltipContainerComponent, this.viewContainer, injector)
		this.overlayRef.attach(component)
	}

	private getPositionStrategy(): PositionStrategy {
		return this.overlay
			.position()
			.flexibleConnectedTo(this.element)
			.withPositions([
				this.positions[this.position()],
				this.positions.top,
				this.positions.bottom,
				this.positions.left,
				this.positions.right,
			])
	}

	get positions(): TooltipPositions {
		return {
			top: {
				originX: 'center',
				originY: 'top',
				overlayX: 'center',
				overlayY: 'bottom',
				panelClass: 'top',
			},
			bottom: {
				originX: 'center',
				originY: 'bottom',
				overlayX: 'center',
				overlayY: 'top',
				panelClass: 'bottom',
			},
			left: {
				originX: 'start',
				originY: 'center',
				overlayX: 'end',
				overlayY: 'center',
				panelClass: 'left',
			},
			right: {
				originX: 'end',
				originY: 'center',
				overlayX: 'start',
				overlayY: 'center',
				panelClass: 'right',
			},
		}
	}
}
